<template>
  <div class="analytics-page">
    <block-buttons
      title="Расширенная аналитика продаж"
      description="Показатели, сравнение периодов и структура продаж"
      :actions="toolbarActions"
    >
      <template #icon>
        <ChartNoAxesCombined :size="22" />
      </template>
    </block-buttons>

    <form
      class="analytics-page__filters"
      @submit.prevent="loadReport"
    >
      <label>
        С
        <input
          v-model="filters.from"
          type="text"
          placeholder="ДД.ММ.ГГГГ"
          maxlength="10"
          required
        />
      </label>

      <label>
        По
        <input
          v-model="filters.to"
          type="text"
          placeholder="ДД.ММ.ГГГГ"
          maxlength="10"
          required
        />
      </label>

      <label>Группировка
        <select v-model="filters.groupBy">
          <option value="day">По дням</option>
          <option value="week">По неделям</option>
          <option value="month">По месяцам</option>
        </select>
      </label>

      <label>
        Продавец
        <select
          v-model="filters.userId"
          :disabled="!users.length"
        >
          <option value="">Все продавцы</option>

          <option
            v-for="user in users"
            :key="user.id"
            :value="String(user.id)"
          >
            {{ `${user.name} ${user.surname}` }}
          </option>
        </select>

      </label>
      <label>
        Топ товаров
        <input
          v-model="filters.topLimit"
          type="number"
          min="1"
          max="50"
          step="1"
          required
        />
      </label>

      <button
        type="submit"
        class="analytics-page__primary"
        :disabled="loading"
      >
        Показать
      </button>

      <p class="analytics-page__filter-help">
        Оба дня включаются, максимум 366 дней.
      </p>
    </form>

    <p class="analytics-page__note">
      Возвраты учитываются по дате исходной продажи и исключаются из выручки, закупочной стоимости, комиссии, оплат и проданных товаров.
      Поэтому возврат может изменить показатели прошлых периодов.
    </p>

    <div
      v-if="loading"
      class="analytics-page__state"
      role="status"
      aria-live="polite"
    >
      Загружаем аналитику продаж…
    </div>

    <div
      v-else-if="error"
      class="analytics-page__state"
      role="alert"
    >
      <strong>{{ error }}</strong>

      <button
        type="button"
        @click="loadReport"
      >
        Попробовать снова
      </button>
    </div>

    <p
      v-else-if="!report"
      class="analytics-page__state"
      role="status"
    >
      Выберите фильтры и нажмите «Показать».
    </p>

    <template v-else>
      <header class="analytics-page__period">
        <h2>{{ periodLabel }}</h2>

        <p>Сравнение с {{ comparisonLabel }} — предыдущим периодом той же длины.</p>
      </header>

      <p
        v-if="emptyReport"
        class="analytics-page__state"
        role="status"
      >
        В выбранном периоде продаж и возвратов нет. Ниже показаны нулевые показатели и сравнение с предыдущим периодом.
      </p>

      <section
        class="analytics-page__metrics"
        aria-label="Показатели и сравнение периодов"
      >
        <article
          v-for="metric in metrics"
          :key="metric.key"
          class="analytics-page__metric"
        >
          <h3>{{ metric.label }}</h3>

          <strong class="analytics-page__value">{{ formatMetric(report.summary[metric.key], metric.unit) }}</strong>

          <p class="analytics-page__previous">Было: {{ previousValue(metric) }}</p>

          <p class="analytics-page__change">
            <span>{{ absoluteChange(metric) }}</span>
            <span>({{ percentChange(metric) }})</span>
          </p>

          <p
            v-if="metric.hint"
            class="analytics-page__hint"
          >
            {{ metric.hint }}
          </p>
        </article>
      </section>

      <p class="analytics-page__hint">Изменения процентных показателей указаны в процентных пунктах. «—» в относительном изменении означает, что показатель предыдущего периода равен нулю или сравнение недоступно.</p>

      <section class="analytics-page__payments" aria-label="Сохранённые суммы оплат">
        <h2>Сохранённые оплаты</h2>

        <dl>
          <div
            v-for="payment in payments"
            :key="payment.key"
          >
            <dt>{{ payment.label }}</dt>
            <dd>{{ paymentValue(payment.key) }}</dd>
          </div>
        </dl>

        <p class="analytics-page__hint">Расхождение — выручка минус сохранённые суммы оплат(скидки и пр).</p>
      </section>

      <section
        class="analytics-page__charts"
        aria-label="Графики продаж"
      >
        <article
          v-for="chart in charts"
          :key="chart.key"
          class="analytics-page__chart-card"
        >
          <h2>{{ chart.title }}</h2>

          <div
            v-if="chartHasData(chart.key)"
            class="analytics-page__canvas"
          >
            <canvas
              :ref="`chart-${chart.key}`"
              role="img"
              :aria-label="chart.title"
            >
              {{ chart.title }}
            </canvas>
          </div>

          <p
            v-else
            class="analytics-page__chart-empty"
          >
            Нет данных для графика за выбранный период.
          </p>
        </article>
      </section>

      <p class="analytics-page__hint">Крайние недели и месяцы могут быть неполными: графики включают только даты выбранного периода.</p>

      <data-table
        :rows="report.topProducts || []"
        :columns="productColumns"
        :search-keys="['title']"
        title="Топ товаров"
        description="По количеству проданных единиц. Сумма чека не распределяется по товарам."
        search-placeholder="Название товара"
        empty-text="Проданных товаров за выбранный период нет"
      />

      <sales-insights :report="report" />

      <data-table
        :rows="report.sellers || []"
        :columns="sellerColumns"
        row-key="userId"
        :search-keys="['sellerName']"
        title="Показатели продавцов"
        description="Все показатели за выбранный период"
        search-placeholder="Имя продавца"
        empty-text="Продаж продавцов за выбранный период нет"
      >
        <template
          v-for="metric in metrics"
          v-slot:[metric.slotName]="{ row }"
        >
          {{ formatMetric(row[metric.key], metric.unit) }}
        </template>
      </data-table>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ChartNoAxesCombined, RefreshCw } from 'lucide-vue'
import Chart from 'chart.js/auto'
import moment from 'moment'
import BlockButtons from '@/components/blockButtons'
import DataTable from '@/components/dataTable'
import SalesInsights from './components/Insights.vue'
import { METRICS, CHARTS } from '@/routes/sale/statistic/helpers/data.js'

import {
  DATE_FORMAT,
  validateFilters,
  requestFilters,
  formatMetric,
  hasChartData,
  chartConfig
} from './analytics'

export default {
  name: 'SaleStatisticV2',
  components: {
    ChartNoAxesCombined,
    BlockButtons,
    SalesInsights,
    DataTable
  },
  data () {
    return {
      filters: {
        from: moment().startOf('month').format(DATE_FORMAT),
        to: moment().format(DATE_FORMAT),
        groupBy: 'day',
        topLimit: 10,
        userId: ''
      },
      report: null,
      loading: false,
      error: '',
      metrics: METRICS.map(metric => ({ ...metric, slotName: `cell-${metric.key}` })),
      charts: CHARTS,
      payments: [
        {
          key: 'terminal',
          label: 'Терминал'
        },
        {
          key: 'cash',
          label: 'Наличные'
        },
        {
          key: 'cardTransfer',
          label: 'Перевод'
        },
        {
          key: 'unallocated',
          label: 'Расхождение с выручкой'
        }
      ],
      productColumns: [
        {
          key: 'title',
          label: 'Товар'
        },
        {
          key: 'units',
          label: 'Продано единиц'
        }
      ]
    }
  },
  computed: {
    ...mapGetters('users', ['getData']),
    users () {
      return this.getData.filter(({ status }) => status !== 'deactivated')
    },
    sellerColumns () {
      return [
        {
          key: 'sellerName',
          label: 'Продавец'
        },
        ...this.metrics.map(metric => ({ key: metric.key, label: metric.label }))
      ]
    },
    toolbarActions () {
      return [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: this.loadReport
        }
      ]
    },
    periodLabel () {
      return this.formatPeriod(this.report?.period)
    },
    comparisonLabel () {
      return this.formatPeriod(this.report?.comparison)
    },
    emptyReport () {
      return !this.report?.summary?.receipts && !this.report?.summary?.returnedReceipts
    }
  },
  watch: {
    filters: {
      deep: true,
      handler () {
        this.invalidateReport()
      }
    }
  },
  created () {
    this.chartInstances = []
  },
  mounted () {
    this.loadReport()
  },
  beforeDestroy () {
    this.invalidateReport()
  },
  methods: {
    ...mapActions('statistic', ['loadExtended']),
    formatMetric,
    formatPeriod (period) {
      if (!period?.from || !period?.to) {
        return '—'
      }

      const from = moment(period.from, DATE_FORMAT, true)
      const to = moment(period.to, DATE_FORMAT, true)

      return from.isValid() && to.isValid()
        ? `${from.format(DATE_FORMAT)} — ${to.format(DATE_FORMAT)}`
        : '—'
    },
    previousValue (metric) {
      return formatMetric(this.report?.comparison?.summary?.[metric.key], metric.unit)
    },
    absoluteChange (metric) {
      return formatMetric(this.report?.comparison?.changes?.[metric.key]?.absolute, metric.unit === 'percent' ? 'points' : metric.unit, true)
    },
    percentChange (metric) {
      return formatMetric(this.report?.comparison?.changes?.[metric.key]?.percent, 'percent', true)
    },
    paymentValue (key) {
      return formatMetric(this.report?.payments?.[key], 'money')
    },
    chartHasData (key) {
      return hasChartData(this.report?.charts?.[key])
    },
    invalidateReport () {
      if (this.loading) {
        this.$bus.emit('stop-loader')
      }

      this.loading = false
      this.error = ''

      this.destroyCharts()
      this.report = null
    },
    async loadReport () {
      this.invalidateReport()
      const payload = requestFilters(this.filters)
      this.error = validateFilters(this.filters)

      if (this.error) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      const { data, error } = await this.loadExtended(payload) || {}

      this.loading = false
      this.$bus.emit('stop-loader')

      if (error) {
        this.error = 'Не удалось загрузить статистику. Попробуйте снова.'

        return
      }

      this.report = data

      await this.$nextTick()

      if (this.report) {
        this.renderCharts()
      }
    },
    destroyCharts () {
      const instances = this.chartInstances || []

      instances.forEach(chart => chart.destroy())

      this.chartInstances = []
    },
    renderCharts () {
      this.destroyCharts()

      this.charts.forEach(chart => {
        if (!this.chartHasData(chart.key)) {
          return
        }

        const refs = this.$refs[`chart-${chart.key}`]
        const canvas = Array.isArray(refs) ? refs[0] : refs

        if (canvas) {
          this.chartInstances.push(new Chart(canvas, chartConfig(chart, this.report.charts[chart.key])))
        }
      })
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
